import * as React from 'react';
import "./topics.scss";
import {
    Accordion,
    AddIcon,
    Button,
    Checkbox,
    ChevronDownIcon,
    CloseIcon,
    Divider,
    EditIcon,
    Flex,
    Header,
    Loader,
    MenuButton,
    MenuItemProps,
    SaveIcon,
    Tooltip,
    TrashCanIcon,
    UndoIcon
} from "@fluentui/react-northstar";
import EditInput from "../Utils/EditInput";
import update from "immutability-helper";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import {ITopic, ITopicTranslation} from "../../Interfaces/Topic";
import {ILanguage} from "../../Interfaces/Language";
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";
import {withRouter, WithRouterProps} from "../../services/TelemetryService/withRouter.hook";

interface INewTopicProps extends WithRouterProps {
}

interface INewTopicState extends ITopic {
    // Is component loaded
    loaded: boolean,
    // Previous changes
    previousChanges: ITopic | undefined,
    // Show cancel dialog
    showCancelDialog: boolean,
    // Show remove dialog
    showRemoveDialog: boolean,
    // Uploading topic
    uploading: boolean,
    // Removing topic
    removing: boolean,
    // Topic ID
    topicId?: string,
    // Is topic linked to messages
    isTopicLinked: boolean,
    // Opened translations panels
    openedTranslations: Map<string, boolean>,
}

class NewTopic extends React.PureComponent<INewTopicProps, INewTopicState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    constructor(props: INewTopicProps, context: IAppContextState) {
        super(props);

        this.state = {
            tenantId: context.teams.tenantId as string,
            loaded: false,
            title: "",
            description: "",
            activateForAllUsers: true,
            cannotDeactivate: true,
            translations: new Array<ITopicTranslation>(),
            showCancelDialog: false,
            showRemoveDialog: false,
            previousChanges: undefined,
            uploading: false,
            removing: false,
            isTopicLinked: false,
            unableToDelete: false,
            openedTranslations: new Map<string, boolean>(),
        }

        this.addTranslation = this.addTranslation.bind(this);
        this.removeTranslation = this.removeTranslation.bind(this);
        this.updateTranslationItem = this.updateTranslationItem.bind(this);
        this.renderTranslationAccordionContent = this.renderTranslationAccordionContent.bind(this);
        this.saveTopic = this.saveTopic.bind(this);
        this.onCancelChanges = this.onCancelChanges.bind(this);
        this.checkChanges = this.checkChanges.bind(this);
        this.removeTopic = this.removeTopic.bind(this);
    }

    async componentDidMount() {
        if (this.props.location.pathname.startsWith("/newtopic/")) {
            const topicId = this.props.location.pathname.split("/")[2];
            this.setState({topicId});
            const topic = this.context.topics.list.find(t => t.id === topicId);
            if (!topic) return;
            this.setState({
                topicId,
                ...topic,
                previousChanges: topic,
                isTopicLinked: topic.unableToDelete,
            });
        } else {
            this.setState({previousChanges: this.state as ITopic});
        }
        this.setState({loaded: true});
    }

    addTranslation(language: ILanguage) {
        // New translation
        const translation: ITopicTranslation = {
            languageId: language.id,
            title: "",
            description: "",
            tenantId: this.context.teams.tenantId as string,
        };
        // Update translations
        const translations = update(this.state.translations, {$push: [translation]});
        const openedTranslations = new Map(this.state.openedTranslations);
        openedTranslations.set(language.id, false);
        this.setState({translations: translations, openedTranslations});
    }

    removeTranslation(index: number) {
        const languageId = this.state.translations[index].languageId;
        const openedTranslations = new Map(this.state.openedTranslations);
        openedTranslations.set(languageId, false);
        this.setState({
            translations: update(this.state.translations, {$splice: [[index, 1]]}),
            openedTranslations
        });
    }

    updateTranslationItem(key: string, value: string, index: number) {
        this.setState({
            translations: update(this.state.translations, {
                [index]: {[key]: {$set: value}}
            })
        })
    }

    async saveTopic() {
        this.setState({uploading: true});
        const topic: ITopic = {
            id: this.state.topicId,
            title: this.state.title,
            description: this.state.description,
            activateForAllUsers: this.state.activateForAllUsers || this.state.cannotDeactivate,
            cannotDeactivate: this.state.cannotDeactivate,
            translations: this.state.translations,
            tenantId: this.context.teams.tenantId as string,
            unableToDelete: false,
        };
        await this.context.topics.addUpdateTopic(topic);
        this.props.navigate("/topics");
    }

    async removeTopic(hasConfirmed: boolean) {
        if (hasConfirmed && this.state.topicId) {
            this.setState({uploading: true, removing: true, showRemoveDialog: false});
            await this.context.topics.deleteTopic(this.state.topicId);
            this.props.navigate("/topics");
        } else {
            this.setState({showRemoveDialog: true});
        }
    }

    checkChanges() {
        // Check if there is a modification
        let hasChanged = this.state.title !== this.state.previousChanges?.title
            || this.state.description !== this.state.previousChanges.description
            || this.state.activateForAllUsers !== this.state.previousChanges.activateForAllUsers
            || this.state.cannotDeactivate !== this.state.previousChanges.cannotDeactivate
            || this.state.translations.length !== this.state.previousChanges.translations.length;
        // Return if there is a modification
        if (hasChanged) return true;
        // Check every translation
        if (this.state.previousChanges) {
            for (let i = 0; i < this.state.translations.length; i++) {
                const current = this.state.translations[i];
                let previousFound = false;
                for (let j = 0; j < this.state.previousChanges.translations.length; j++) {
                    let previous = this.state.previousChanges.translations[i];
                    if (current.languageId === previous.languageId) {
                        previousFound = true;
                        if (current.title !== previous.title || current.description !== previous.description)
                            return true;
                    }
                }
                if (!previousFound) return true;
            }
        }
        return false;
    }

    onCancelChanges() {
        let hasChanged = this.checkChanges();
        if (hasChanged) this.setState({showCancelDialog: true});
        else this.props.navigate("/topics")
    }

    handleClickTranslation = (index: number) => {
        const openedTranslations = new Map(this.state.openedTranslations);
        const languageId = this.state.translations[index].languageId;
        openedTranslations.set(languageId, !openedTranslations.get(languageId));
        this.setState({openedTranslations})
    }

    handleAddTranslation = (e: React.SyntheticEvent, data: MenuItemProps | undefined) => {
        const language: ILanguage | undefined = (data as { value: ILanguage | undefined }).value;
        if (!language) return;
        this.addTranslation(language);
    }

    /**
     * Render component
     */
    render() {
        // Show save button
        let showSaveButton = this.state.title.length > 0 && this.state.description.length > 0;

        // Check every translation
        this.state.translations.forEach((value) => {
            if (value.title.length === 0 || value.description.length === 0)
                showSaveButton = false;
        })

        // Topic translations
        const translations: Array<string> = this.state.translations.map(t => {
            const language = this.context.languages.list.find(l => l.id === t.languageId);
            return language?.code ?? "";
        }).filter(t => !!t);

        // Available languages
        const availableLanguages = this.context.languages.list
            .filter(l => l.code !== "EN" && !translations.includes(l.code))
            .map(l => ({
                key: "language_" + l.code,
                content: languageObject.translate(l.languageName),
                value: l
            }));

        const openedTranslationsIndexes = new Array<number>();
        this.state.translations.forEach((translation, index) => {
            const isOpen = this.state.openedTranslations.get(translation.languageId);
            if (isOpen) openedTranslationsIndexes.push(index);
        });

        return (
            <Flex column fill gap={"gap.large"} className={"p-20px"}>
                <Flex className={"form_layout"} column fill>
                    <Flex gap={"gap.small"} space={"between"} wrap>
                        <Flex styles={{whiteSpace: "nowrap"}} gap={"gap.small"} vAlign={"center"}>
                            <EditIcon styles={{color: "#782753"}} size={"large"}/>
                            <Header as={"h2"} styles={{margin: 0}}
                                    content={languageObject.translate(this.state.topicId ? "UpdateTopic" : "CreateNewTopic")}/>
                        </Flex>
                        {!this.state.loaded ? null :
                            <Flex hAlign={"end"} gap={"gap.small"} vAlign={"center"}>
                                {this.state.topicId ?
                                    this.state.isTopicLinked ?
                                        <Tooltip content={languageObject.translate("TopicLinked")} trigger={
                                            <Flex>
                                                <Button loading={this.state.removing} icon={<TrashCanIcon outline/>}
                                                        disabled={this.state.uploading || this.state.isTopicLinked}
                                                        className={this.state.uploading || this.state.isTopicLinked ? "" : "delete_button"}
                                                        content={languageObject.translate("Remove")}
                                                        onClick={() => this.removeTopic(false)}/>
                                            </Flex>
                                        }/>
                                        :
                                        <Button loading={this.state.removing} icon={<TrashCanIcon outline/>}
                                                disabled={this.state.uploading || this.state.isTopicLinked}
                                                className={this.state.uploading || this.state.isTopicLinked ? "" : "delete_button"}
                                                content={languageObject.translate("Remove")}
                                                onClick={() => this.removeTopic(false)}/>
                                    :
                                    null
                                }
                                <Button
                                    loading={!this.state.removing && this.state.uploading}
                                    icon={<SaveIcon outline/>}
                                    disabled={!showSaveButton || this.state.uploading}
                                    content={languageObject.translate("Save")}
                                    onClick={() => this.saveTopic()} primary
                                />
                                <Button disabled={this.state.uploading} icon={<CloseIcon outline/>}
                                        content={languageObject.translate("Close")}
                                        onClick={() => this.onCancelChanges()}/>
                            </Flex>
                        }
                    </Flex>
                    {!this.state.loaded ?
                        <Flex fill vAlign={"center"} hAlign={"center"}>
                            <Loader/>
                        </Flex>
                        :
                        <>
                            <EditInput
                                required
                                inverted
                                max={50}
                                disabled={this.state.uploading}
                                label={languageObject.translate("TitleText")}
                                value={this.state.title}
                                setValue={value => this.setState({title: value})}/>
                            <EditInput
                                required
                                inverted
                                max={100}
                                disabled={this.state.uploading}
                                label={languageObject.translate("Description")}
                                value={this.state.description}
                                setValue={value => this.setState({description: value})}/>
                            <Flex>
                                <Checkbox checked={this.state.cannotDeactivate} className={"form-input"}
                                          disabled={this.state.uploading}
                                          label={languageObject.translate("CannotDeactivate")}
                                          onClick={() => this.setState({cannotDeactivate: !this.state.cannotDeactivate})}/>
                            </Flex>
                            <Flex>
                                <Checkbox checked={this.state.activateForAllUsers || this.state.cannotDeactivate}
                                          className={"form-input"}
                                          disabled={this.state.uploading || this.state.cannotDeactivate}
                                          label={languageObject.translate("ActivatedAllUsersDefault")}
                                          onClick={() => this.setState({activateForAllUsers: !this.state.activateForAllUsers})}/>
                            </Flex>
                            <Divider content={languageObject.translate("Translations")}/>
                            <MenuButton
                                id={"add_translation"}
                                trigger={
                                    <Button
                                        primary
                                        disabled={this.state.uploading || availableLanguages.length === 0}
                                        icon={<AddIcon outline/>}
                                        className={"form-input"}
                                        content={
                                            <Flex hAlign={"center"} vAlign={"center"}>
                                                {languageObject.translate("AddTranslation")}
                                                <ChevronDownIcon xSpacing={"before"}/>
                                            </Flex>
                                        }
                                    />
                                }
                                menu={availableLanguages}
                                onMenuItemClick={this.handleAddTranslation}
                            />
                            <Accordion
                                className={"form-input"}
                                activeIndex={openedTranslationsIndexes}
                                panels={this.state.translations.map((t, index) => {
                                    const language = this.context.languages.list.find(l => l.id === t.languageId);
                                    return {
                                        title: {
                                            key: "translation_title_" + t.languageId,
                                            content: languageObject.translate(language?.languageName ?? "?"),
                                            accordionContentId: "translation_content_" + t.languageId,
                                            onClick: () => this.handleClickTranslation(index)
                                        },
                                        content: {
                                            key: "translation_content_" + t.languageId,
                                            content: this.renderTranslationAccordionContent(index),
                                            accordionTitleId: "translation_title_" + t.languageId,
                                        }
                                    }
                                })}/>
                        </>
                    }
                </Flex>
                {this.renderConfirmRemoveDialog()}
                {this.renderDiscardEditsDialog()}
            </Flex>
        );
    }

    public renderConfirmRemoveDialog = () => (
        <ConfirmDialog
            show={this.state.showRemoveDialog}
            title={languageObject.translate("RemoveTopic")}
            subtitle={this.context.topics.list.find(t => t.id === this.state.topicId)?.title}
            content={languageObject.translate("ConfirmRemoveTopic")}
            onCancel={() => this.setState({showRemoveDialog: false})}
            onConfirm={() => this.removeTopic(true)}
            button={{
                icon: <TrashCanIcon outline/>,
                title: languageObject.translate("Remove"),
                className: "delete_button",
            }}
        />
    )

    public renderDiscardEditsDialog = () => (
        <ConfirmDialog
            show={this.state.showCancelDialog}
            title={languageObject.translate("UnsavedChanges")}
            subtitle={this.context.topics.list.find(t => t.id === this.state.topicId)?.title}
            content={languageObject.translate("DiscardEdits")}
            onCancel={() => this.setState({showCancelDialog: false})}
            onConfirm={() => this.props.navigate("/topics")}
            button={{
                title: languageObject.translate("Discard"),
                icon: <UndoIcon outline/>,
            }}
        />
    )

    public renderTranslationAccordionContent = (index: number) => {
        const translation = this.state.translations[index];
        if (!translation) return null;
        return (
            <Flex column styles={{marginBottom: "20px !important"}}>
                <EditInput
                    required
                    inverted
                    max={50}
                    disabled={this.state.uploading}
                    label={languageObject.translate("TitleText")}
                    value={translation.title}
                    setValue={value => this.updateTranslationItem("title", value, index)}/>
                <EditInput
                    required
                    inverted
                    max={100}
                    disabled={this.state.uploading}
                    label={languageObject.translate("Description")}
                    value={translation.description}
                    setValue={value => this.updateTranslationItem("description", value, index)}/>
                <Flex fill gap={"gap.small"} hAlign={"end"}>
                    <Button
                        icon={<TrashCanIcon outline/>}
                        disabled={this.state.uploading}
                        className={this.state.uploading ? "form-input" : "delete_button form-input"}
                        content={languageObject.translate("Remove")}
                        onClick={() => this.removeTranslation(index)}/>
                </Flex>
            </Flex>
        );
    }
}

export default withRouter(NewTopic)